import { TaskGroupCreateSchema } from "@common/contracts/task-group.contract";
import { TaskGroup } from "@common/types/taskGroup";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import {
    useCreateWorkspaceTaskGroupMutation,
    useUpdateWorkspaceTaskGroupMutation,
} from "@api/taskGroup.api";
import {
    useCreateOrganizationTaskGroupMutation,
    useUpdateOrganizationTaskGroupMutation,
} from "@api/organizationTaskGroup.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { StandardModalHeader, PlainModal } from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";

type TaskGroupFormValues = z.infer<typeof TaskGroupCreateSchema>;

const fields = createFormikFields<TaskGroupFormValues>();

interface Props {
    show: boolean;
    onHide: () => void;
    taskGroup?: TaskGroup;
    scope: "workspace" | "organization";
}

export const TaskGroupFormModal = ({
    show,
    onHide,
    taskGroup,
    scope,
}: Props) => {
    const { error: errorToast } = useToaster();
    const createWorkspaceTaskGroup = useCreateWorkspaceTaskGroupMutation();
    const createOrganizationTaskGroup =
        useCreateOrganizationTaskGroupMutation();
    const updateWorkspaceTaskGroup =
        useUpdateWorkspaceTaskGroupMutation(taskGroup);
    const updateOrganizationTaskGroup =
        useUpdateOrganizationTaskGroupMutation(taskGroup);

    const createMutation =
        scope === "workspace"
            ? createWorkspaceTaskGroup
            : createOrganizationTaskGroup;
    const updateMutation =
        scope === "workspace"
            ? updateWorkspaceTaskGroup
            : updateOrganizationTaskGroup;

    const isEditMode = !!taskGroup;
    const isSubmitting = createMutation.isPending || updateMutation.isPending;

    const formik = useFormik<TaskGroupFormValues>({
        initialValues: {
            name: taskGroup?.name ?? "",
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(TaskGroupCreateSchema),
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: isSubmitting,
            handler: async (values) => {
                if (isEditMode && taskGroup) {
                    await updateMutation.mutateAsync({
                        name: values.name,
                    });
                } else {
                    await createMutation.mutateAsync({
                        name: values.name,
                    });
                }

                onHide();
            },
            failureHandler: async () => {
                errorToast(
                    isEditMode
                        ? "Failed to update task group"
                        : "Failed to create task group",
                );
            },
        }),
    });

    return (
        <PlainModal show={show} onHide={onHide}>
            <StandardModalHeader>
                {isEditMode ? "Edit task group" : "Create task group"}
            </StandardModalHeader>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <VStack gap={16}>
                        <fields.Text name="name" label="Name" size="xl" />
                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onHide}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                type="submit"
                                loading={isSubmitting}
                                disabled={!formik.isValid}
                            >
                                {isEditMode ? "Save" : "Create"}
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
